import { DamageDetail, InspectionDetail } from '../MoveOut/types'

/** Tenant-reported issue during move-in (from the Issue model). */
export interface FlattenedIssue {
    id: number
    config_name: string
    description: string
    area_name: string
    has_photo: boolean
    inventory_name: string
}

/** Inventory item flagged by inspector status (CustomStatus.causes_flag). */
export interface FlattenedFlaggedItem {
    id: number
    inventory_name: string
    status: string | null
    area_name: string
    note: string
    has_photo: boolean
}

export type InspectionPreviewItem = {
    id: number | string
    label: string
    area_name: string
}

export const flattenTenantIssuesFromDetails = (
    details: InspectionDetail[],
): FlattenedIssue[] => {
    const issues: FlattenedIssue[] = []
    for (const area of details) {
        for (const inv of area.inventory_inspections ?? []) {
            for (const issue of inv.issues ?? []) {
                issues.push({
                    id: issue.id,
                    config_name: issue.config_name,
                    description: issue.description,
                    area_name: area.area_name,
                    has_photo: (issue.attachments ?? []).length > 0,
                    inventory_name: inv.name,
                })
            }
        }
    }
    return issues
}

export const flattenFlaggedItemsFromDetails = (
    details: InspectionDetail[],
): FlattenedFlaggedItem[] => {
    const flagged: FlattenedFlaggedItem[] = []
    for (const area of details) {
        for (const inv of area.inventory_inspections ?? []) {
            if (!inv.causes_flag) {
                continue
            }
            flagged.push({
                id: inv.id,
                inventory_name: inv.name,
                status: inv.status,
                area_name: area.area_name,
                note: inv.note,
                has_photo: (inv.attachments ?? []).length > 0,
            })
        }
    }
    return flagged
}

export const tenantIssuesToPreviewItems = (
    issues: FlattenedIssue[],
): InspectionPreviewItem[] =>
    issues.map((issue) => ({
        id: issue.id,
        label: issue.config_name,
        area_name: issue.area_name,
    }))

export const flaggedItemsToPreviewItems = (
    items: FlattenedFlaggedItem[],
): InspectionPreviewItem[] =>
    items.map((item) => ({
        id: item.id,
        label: item.status
            ? `${item.inventory_name} (${item.status})`
            : item.inventory_name,
        area_name: item.area_name,
    }))

const formatCurrency = (value: number | string) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(Number(value))

export const damagesToPreviewItems = (
    damages: DamageDetail[],
): InspectionPreviewItem[] =>
    damages.map((damage) => ({
        id: damage.id,
        label: `${damage.description} (${formatCurrency(damage.total_cost)})`,
        area_name: damage.area_name,
    }))

export const formatDamageTotal = (total: string): string => formatCurrency(total)

/** @deprecated Use flattenTenantIssuesFromDetails or flattenFlaggedItemsFromDetails */
export const flattenIssuesFromDetails = flattenTenantIssuesFromDetails

export const countTenantIssues = (details: InspectionDetail[]): number =>
    flattenTenantIssuesFromDetails(details).length

export const countFlaggedItems = (details: InspectionDetail[]): number =>
    flattenFlaggedItemsFromDetails(details).length

export const getLatestCompletedDate = (
    details: InspectionDetail[],
): string | null => {
    const dates = details
        .map((d) => d.completed_date)
        .filter((d): d is string => !!d)
    if (dates.length === 0) {
        return null
    }
    return dates.sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    )[0]
}

export const getEarliestDueDate = (
    details: InspectionDetail[],
): string | null => {
    const dates = details
        .map((d) => d.due_date)
        .filter((d): d is string => !!d)
    if (dates.length === 0) {
        return null
    }
    return dates.sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    )[0]
}

export const countAreas = (details: InspectionDetail[]): number =>
    details.length

export const isOverdue = (endDate: string) => new Date(endDate) < new Date()

export type ResultsFilterMode = 'tenant_issues' | 'flagged'

export const isInventoryItemNotable = (
    inv: InspectionDetail['inventory_inspections'][number],
    mode: ResultsFilterMode,
): boolean => {
    if (mode === 'tenant_issues') {
        return (inv.issues ?? []).length > 0
    }
    return Boolean(inv.causes_flag)
}

export const countNotableItems = (
    details: InspectionDetail[],
    mode: ResultsFilterMode,
): number => {
    let count = 0
    for (const area of details) {
        for (const inv of area.inventory_inspections ?? []) {
            if (isInventoryItemNotable(inv, mode)) {
                count += 1
            }
        }
    }
    return count
}
